import { useEffect, useRef, useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { Printer, BookOpen, Download, Share2, X } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { nodeToPdf, buildStudyBook } from "../lib/pdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEMES = {
  red:    { bar: "bg-[#FF003C] text-white",   tile: "bg-white",   stripe: "stripe-red" },
  yellow: { bar: "bg-[#FFD700] text-black",   tile: "bg-white",   stripe: "stripe-bg" },
  orange: { bar: "bg-[#FF8A00] text-black",   tile: "bg-white",   stripe: "stripe-bg" },
  cyan:   { bar: "bg-[#00E5FF] text-black",   tile: "bg-white",   stripe: "stripe-bg" },
  black:  { bar: "bg-zinc-950 text-white",    tile: "bg-zinc-50", stripe: "stripe-bg" },
};

export default function Posters() {
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shared, setShared] = useState(null);
  const [bookProgress, setBookProgress] = useState(null);

  useEffect(() => {
    axios.get(`${API}/posters`).then((r) => {
      setPosters(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const generateBook = async () => {
    setBookProgress({ current: 0, total: 0, stage: "loading" });
    const r = await axios.get(`${API}/elements/full`);
    setBookProgress({ current: 0, total: r.data.length, stage: "building" });
    buildStudyBook(r.data, (current, total) => setBookProgress({ current, total, stage: "building" }));
    setTimeout(() => setBookProgress(null), 1500);
  };

  return (
    <div className="bg-white min-h-screen" data-testid="posters-page">
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" />
            Page 30 · Safety Reference Posters
          </div>
          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
            30 Posters.<br />
            <span className="bg-[#FFD700] px-2 inline-block">Major Hazards.</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-xl">
            Print-ready warning, mandatory and emergency posters. Download any poster as PDF, share via QR,
            or get the full <strong>Study Book</strong> covering all 118 elements.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => window.print()}
              data-testid="print-posters"
              className="bg-white text-zinc-950 font-mono text-xs uppercase tracking-widest px-6 py-3 border-2 border-zinc-950 hover:neo-shadow hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
            >
              <Printer size={14} strokeWidth={2.5} /> Print All
            </button>
            <button
              onClick={generateBook}
              data-testid="download-study-book"
              disabled={bookProgress?.stage === "building"}
              className="bg-zinc-950 text-white font-mono text-xs uppercase tracking-widest px-6 py-3 border-2 border-zinc-950 hover:bg-zinc-800 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
            >
              <BookOpen size={14} strokeWidth={2.5} />
              {bookProgress?.stage === "building"
                ? `Building Book… ${bookProgress.current}/${bookProgress.total}`
                : "Download Study Book PDF"}
            </button>
          </div>
        </div>
      </section>

      <section className="bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {loading ? (
            <div className="font-mono text-sm text-zinc-600 py-12 text-center">Loading posters…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posters.map((p) => (
                <PosterCard key={p.id} poster={p} onShare={() => setShared(p)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <ShareDialog poster={shared} open={!!shared} onClose={() => setShared(null)} />
    </div>
  );
}

const PosterCard = ({ poster, onShare }) => {
  const theme = THEMES[poster.theme] || THEMES.yellow;
  const IconCmp = Icons[poster.icon] || Icons.AlertTriangle;
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePdf = async () => {
    setBusy(true);
    try {
      await nodeToPdf(ref.current, `safety-poster-${String(poster.id).padStart(2, "0")}-${poster.title.toLowerCase().replace(/\s+/g, "-")}.pdf`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={ref}
        data-testid="safety-poster-card"
        className={`relative ${theme.tile} border-2 border-zinc-950 neo-shadow flex flex-col`}
        style={{ aspectRatio: "3/4" }}
      >
        <div className={`${theme.stripe} h-3 border-b-2 border-zinc-950`} />
        <div className="absolute top-3 right-3 bg-white border-2 border-zinc-950 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 z-10">
          № {String(poster.id).padStart(2, "0")}
        </div>
        <div className={`${theme.bar} border-b-2 border-zinc-950 px-5 py-6 flex items-center justify-center`}>
          <IconCmp size={72} strokeWidth={2.75} />
        </div>
        <div className="px-5 pt-4 pb-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">{poster.subtitle}</div>
          <h3 className="font-heading font-black text-2xl lg:text-3xl leading-[0.95] tracking-tighter uppercase mt-1 text-zinc-950">
            {poster.title}
          </h3>
        </div>
        <div className="px-5">
          <div className="border-2 border-zinc-950 bg-zinc-950 text-white px-3 py-2 font-mono text-[11px] font-bold tracking-widest uppercase text-center">
            {poster.warning}
          </div>
        </div>
        <ul className="px-5 py-4 space-y-1.5 flex-1">
          {poster.rules.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-[12px] leading-snug text-zinc-900">
              <span className="font-mono font-bold text-zinc-950 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
        <div className="border-t-2 border-zinc-950 px-5 py-2 flex items-center justify-between bg-zinc-50">
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">Safety Reference</div>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">CHEM/SAFETY · v1</div>
        </div>
      </div>

      <div className="flex gap-2 print:hidden">
        <button
          data-testid={`poster-pdf-${poster.id}`}
          onClick={handlePdf}
          disabled={busy}
          className="flex-1 bg-white border-2 border-zinc-950 px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center justify-center gap-1.5 hover:neo-shadow hover:-translate-y-0.5 transition-all disabled:opacity-60"
        >
          <Download size={12} strokeWidth={2.5} /> {busy ? "…" : "PDF"}
        </button>
        <button
          data-testid={`poster-qr-${poster.id}`}
          onClick={onShare}
          className="flex-1 bg-zinc-950 text-white border-2 border-zinc-950 px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center justify-center gap-1.5 hover:bg-zinc-800"
        >
          <Share2 size={12} strokeWidth={2.5} /> QR Share
        </button>
      </div>
    </div>
  );
};

const ShareDialog = ({ poster, open, onClose }) => {
  if (!poster) return null;
  const url = `${window.location.origin}/posters?id=${poster.id}`;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 border-2 border-zinc-950 rounded-none bg-white" data-testid="poster-share-dialog">
        <DialogTitle className="sr-only">Share poster {poster.title}</DialogTitle>
        <DialogDescription className="sr-only">Scan the QR code to open this safety poster on a mobile device.</DialogDescription>
        <div className="bg-zinc-950 text-white p-5 flex items-start justify-between border-b-2 border-zinc-950">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-70 mb-1">Share via QR</div>
            <div className="font-heading font-bold text-xl uppercase tracking-tight">{poster.title}</div>
          </div>
          <button onClick={onClose} className="border-2 border-white p-1.5 hover:bg-white hover:text-zinc-950" data-testid="qr-close">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
        <div className="p-8 flex flex-col items-center bg-white">
          <div className="p-4 bg-white border-2 border-zinc-950">
            <QRCodeCanvas value={url} size={220} level="H" includeMargin={false} />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-4 text-center break-all">
            {url}
          </div>
          <button
            onClick={() => navigator.clipboard?.writeText(url)}
            className="mt-4 bg-zinc-950 text-white font-mono text-xs uppercase tracking-widest px-5 py-2 border-2 border-zinc-950 hover:bg-zinc-800"
            data-testid="qr-copy-url"
          >
            Copy Link
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
